import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiRefreshCw, FiDownload, FiCalendar, FiChevronDown, FiChevronRight, FiFilter } from 'react-icons/fi';
import reportesContablesService from '../services/reportesContablesService';
import Loading from '../components/Loading';
import './AnalisisCuentasPage.css';

const AnalisisCuentasPage = () => {
  const [analisisData, setAnalisisData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCuentas, setExpandedCuentas] = useState(new Set());
  const [exportando, setExportando] = useState(false);

  // Fechas por defecto (último mes)
  const hoy = new Date();
  const haceUnMes = new Date();
  haceUnMes.setMonth(haceUnMes.getMonth() - 1);

  const [fechaDesde, setFechaDesde] = useState(haceUnMes.toISOString().split('T')[0]);
  const [fechaHasta, setFechaHasta] = useState(hoy.toISOString().split('T')[0]);
  const [tipoCuenta, setTipoCuenta] = useState('');
  const [soloConMovimientos, setSoloConMovimientos] = useState(true);

  useEffect(() => {
    cargarAnalisis();
  }, []);

  const cargarAnalisis = async () => {
    try {
      setLoading(true);
      const response = await reportesContablesService.getAnalisisCuentas(
        fechaDesde || null,
        fechaHasta,
        tipoCuenta || null,
        soloConMovimientos
      );
      setAnalisisData(response.data);
    } catch (error) {
      console.error('Error al cargar análisis de cuentas:', error);
      toast.error(error.response?.data?.error || 'Error al cargar el análisis de cuentas');
    } finally {
      setLoading(false);
    }
  };

  const handleFiltrar = () => {
    cargarAnalisis();
  };

  const toggleCuenta = (cuentaId) => {
    const newExpanded = new Set(expandedCuentas);
    if (newExpanded.has(cuentaId)) {
      newExpanded.delete(cuentaId);
    } else {
      newExpanded.add(cuentaId);
    }
    setExpandedCuentas(newExpanded);
  };

  const expandirTodo = () => {
    if (analisisData && analisisData.cuentas) {
      const allIds = new Set(analisisData.cuentas.map(c => c.id));
      setExpandedCuentas(allIds);
    }
  };

  const colapsarTodo = () => {
    setExpandedCuentas(new Set());
  };

  const handleExportarExcel = async () => {
    try {
      setExportando(true);
      const XLSX = await import('xlsx');

      // Preparar datos para Excel
      const datos = analisisData.cuentas.map(cuenta => ({
        Código: cuenta.codigo,
        Cuenta: cuenta.nombre,
        Tipo: cuenta.tipo_cuenta,
        'Total Debe': cuenta.total_debe,
        'Total Haber': cuenta.total_haber,
        Saldo: cuenta.saldo,
        'Movimientos': cuenta.num_movimientos,
        Estado: cuenta.esta_balanceada ? 'Balanceada' : 'Desbalanceada'
      }));

      // Crear workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(datos);

      XLSX.utils.book_append_sheet(wb, ws, 'Análisis de Cuentas');

      // Descargar
      XLSX.writeFile(wb, `analisis-cuentas-${fechaDesde}-${fechaHasta}.xlsx`);

      toast.success('Análisis exportado a Excel correctamente');
    } catch (error) {
      console.error('Error al exportar Excel:', error);
      toast.error('Error al exportar a Excel');
    } finally {
      setExportando(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('es-CL');
  };

  if (loading) {
    return <Loading />;
  }

  if (!analisisData) {
    return (
      <div className="analisis-cuentas-page">
        <div className="error-message">No se pudo cargar el análisis de cuentas</div>
      </div>
    );
  }

  return (
    <div className="analisis-cuentas-page">
      {/* Header */}
      <div className="analisis-header">
        <div className="analisis-title-section">
          <h1>Análisis de Cuentas</h1>
          <p className="analisis-subtitle">{analisisData.cuenta.nombre}</p>
        </div>
      </div>

      {/* Filtros */}
      <div className="filtros-section">
        <div className="filtro-group">
          <label><FiCalendar /> Desde:</label>
          <input
            type="date"
            value={fechaDesde}
            onChange={(e) => setFechaDesde(e.target.value)}
            className="filtro-input"
          />
        </div>

        <div className="filtro-group">
          <label><FiCalendar /> Hasta:</label>
          <input
            type="date"
            value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)}
            className="filtro-input"
          />
        </div>

        <div className="filtro-group">
          <label><FiFilter /> Tipo de Cuenta:</label>
          <select
            value={tipoCuenta}
            onChange={(e) => setTipoCuenta(e.target.value)}
            className="filtro-select"
          >
            <option value="">Todas</option>
            <option value="ACTIVO">Activo</option>
            <option value="PASIVO">Pasivo</option>
            <option value="PATRIMONIO">Patrimonio</option>
            <option value="INGRESO">Ingreso</option>
            <option value="GASTO">Gasto</option>
          </select>
        </div>

        <div className="filtro-group checkbox-group">
          <label>
            <input
              type="checkbox"
              checked={soloConMovimientos}
              onChange={(e) => setSoloConMovimientos(e.target.checked)}
            />
            Solo cuentas con movimientos
          </label>
        </div>

        <button onClick={handleFiltrar} className="btn-filtrar">
          <FiFilter /> Filtrar
        </button>
      </div>

      {/* Resumen */}
      <div className="resumen-cards">
        <div className="resumen-card">
          <div className="resumen-label">Total Cuentas</div>
          <div className="resumen-value">{analisisData.resumen.total_cuentas}</div>
        </div>
        <div className="resumen-card">
          <div className="resumen-label">Total Debe</div>
          <div className="resumen-value debe">{formatCurrency(analisisData.resumen.total_debe)}</div>
        </div>
        <div className="resumen-card">
          <div className="resumen-label">Total Haber</div>
          <div className="resumen-value haber">{formatCurrency(analisisData.resumen.total_haber)}</div>
        </div>
        <div className="resumen-card">
          <div className="resumen-label">Saldo Total</div>
          <div className={`resumen-value ${analisisData.resumen.total_saldo === 0 ? 'balanceado' : 'desbalanceado'}`}>
            {formatCurrency(analisisData.resumen.total_saldo)}
          </div>
        </div>
        <div className="resumen-card">
          <div className="resumen-label">Balanceadas</div>
          <div className="resumen-value balanceado">{analisisData.resumen.cuentas_balanceadas}</div>
        </div>
        <div className="resumen-card">
          <div className="resumen-label">Desbalanceadas</div>
          <div className="resumen-value desbalanceado">{analisisData.resumen.cuentas_desbalanceadas}</div>
        </div>
      </div>

      {/* Controles */}
      <div className="controles-tabla">
        <div className="expand-controls">
          <button onClick={expandirTodo} className="btn-expand">Expandir Todo</button>
          <button onClick={colapsarTodo} className="btn-collapse">Colapsar Todo</button>
        </div>

        <button onClick={handleExportarExcel} className="btn-exportar" disabled={exportando}>
          <FiDownload /> {exportando ? 'Exportando...' : 'Exportar a Excel'}
        </button>
      </div>

      {/* Tabla de Cuentas */}
      <div className="cuentas-table-container">
        <table className="cuentas-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}></th>
              <th style={{ width: '15%' }}>Código</th>
              <th style={{ width: '30%' }}>Cuenta</th>
              <th style={{ width: '10%' }}>Tipo</th>
              <th style={{ width: '12%' }}>Total Debe</th>
              <th style={{ width: '12%' }}>Total Haber</th>
              <th style={{ width: '12%' }}>Saldo</th>
              <th style={{ width: '9%' }}>Estado</th>
            </tr>
          </thead>
          <tbody>
            {analisisData.cuentas.length > 0 ? (
              analisisData.cuentas.map((cuenta) => (
                <React.Fragment key={cuenta.id}>
                  {/* Fila principal de la cuenta */}
                  <tr className={`cuenta-row ${!cuenta.esta_balanceada ? 'desbalanceada' : ''}`}>
                    <td className="expand-cell">
                      {cuenta.num_movimientos > 0 && (
                        <button
                          onClick={() => toggleCuenta(cuenta.id)}
                          className="btn-expand-row"
                        >
                          {expandedCuentas.has(cuenta.id) ? <FiChevronDown /> : <FiChevronRight />}
                        </button>
                      )}
                    </td>
                    <td className="codigo">{cuenta.codigo}</td>
                    <td className="nombre">{cuenta.nombre}</td>
                    <td className="tipo">
                      <span className={`tipo-badge ${cuenta.tipo_cuenta.toLowerCase()}`}>
                        {cuenta.tipo_cuenta}
                      </span>
                    </td>
                    <td className="monto debe">{formatCurrency(cuenta.total_debe)}</td>
                    <td className="monto haber">{formatCurrency(cuenta.total_haber)}</td>
                    <td className={`monto saldo ${cuenta.saldo === 0 ? 'balanceado' : ''}`}>
                      {formatCurrency(cuenta.saldo)}
                    </td>
                    <td className="estado">
                      {cuenta.esta_balanceada ? (
                        <span className="estado-badge balanceado">✓ Balanceada</span>
                      ) : (
                        <span className="estado-badge desbalanceado">✗ Desbalanceada</span>
                      )}
                    </td>
                  </tr>

                  {/* Filas de detalle de movimientos */}
                  {expandedCuentas.has(cuenta.id) && cuenta.movimientos.length > 0 && (
                    <tr className="detalle-row">
                      <td colSpan="8">
                        <div className="movimientos-detalle">
                          <h4>Movimientos ({cuenta.num_movimientos})</h4>
                          <table className="movimientos-table">
                            <thead>
                              <tr>
                                <th>Asiento</th>
                                <th>Fecha</th>
                                <th>Glosa</th>
                                <th>Proyecto</th>
                                <th>Tipo</th>
                                <th>Monto</th>
                              </tr>
                            </thead>
                            <tbody>
                              {cuenta.movimientos.map((mov, idx) => (
                                <tr key={idx}>
                                  <td>{mov.asiento_numero}</td>
                                  <td>{formatDate(mov.fecha)}</td>
                                  <td>{mov.glosa}</td>
                                  <td>{mov.proyecto || '-'}</td>
                                  <td>
                                    <span className={`mov-tipo ${mov.tipo_movimiento}`}>
                                      {mov.tipo_movimiento === 'debe' ? 'DEBE' : 'HABER'}
                                    </span>
                                  </td>
                                  <td className={`monto ${mov.tipo_movimiento}`}>
                                    {formatCurrency(mov.monto)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="no-data">
                  No hay cuentas con movimientos en el período seleccionado
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="analisis-footer">
        <small>
          Período: {analisisData.periodo.desde ? formatDate(analisisData.periodo.desde) + ' - ' : ''}
          {formatDate(analisisData.periodo.hasta)} |
          Sistema de Contabilidad
        </small>
      </div>
    </div>
  );
};

export default AnalisisCuentasPage;
